import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthContext } from "@/contexts/AuthContext";

/**
 * Clears React Query cache when the authenticated user changes.
 * Prevents stale cross-user/team data being displayed after logout/login.
 *
 * Defensive: Does NOT clear cache when user transiently becomes null during
 * app resume / token refresh. Only clears on actual user switch (user A → user B)
 * or explicit sign-out (user A → null with no pending auth).
 */
export function AuthQueryCacheBuster(): null {
  const { user, loading } = useAuthContext();
  const queryClient = useQueryClient();
  const prevUserId = useRef<string | null>(null);

  useEffect(() => {
    const nextUserId = user?.id ?? null;

    // Skip transient null during auth loading (app resume, token refresh).
    // Only act when auth has settled.
    if (loading) return;

    if (prevUserId.current !== null && nextUserId !== null && prevUserId.current !== nextUserId) {
      // Actual user switch (user A → user B): clear everything
      queryClient.clear();
    } else if (prevUserId.current !== null && nextUserId === null) {
      // Sign-out (user → null after auth settled): clear everything
      queryClient.clear();
    }

    prevUserId.current = nextUserId;
  }, [user?.id, loading, queryClient]);

  return null;
}
